import { Box, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { useNotes } from "api/services/useNotes";
import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { BlockUI } from "components/common/BlockUI";
import { useAppSelector } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { LocalNote, LocalNotes, Note, NoteOwner } from "types/notes";
import { User } from "types/users";
import { generateId } from "utils/generateId";
import { convertArrayToObject } from "utils/notesUtils";

import { CustomEditorNew, SaveNoteData } from "./CustomEditor/CustomEditorNew";
import { NoteItem } from "./NoteItem/NoteItem";
import { SortableList } from "./SortableComponents/SortableContainer";

export interface NotesSectionProps {
  detectorId?: string;
  notesData?: Note[] | LocalNote[];
  setNotesData?: React.Dispatch<React.SetStateAction<LocalNotes>>;
  isLocalModeActive?: boolean;
  ownerNote: NoteOwner;
  ownerGroupId?: string;
  ownerGroupName?: string;
  isLoadingNotes: boolean;
}

export function NotesSection(props: NotesSectionProps) {
  const {
    detectorId,
    notesData,
    setNotesData,
    isLocalModeActive,
    ownerNote,
    ownerGroupId,
    ownerGroupName,
    isLoadingNotes,
  } = props;

  const [notesList, setNotesList] = useState<Note[] | LocalNote[] | undefined>(notesData);

  const userData: User = useAppSelector(getUserState);

  const isOwner = !!userData.groups.find((group) => group.id === ownerGroupId) || !!isLocalModeActive;
  const canEditGLNotes = userData.can_edit_gl_notes;

  const { createNote, isCreatingNote, updateNotesOrder, isUpdatingNotesOrder } = useNotes({ detectorId });

  const canModify = isOwner || (canEditGLNotes && ownerNote === NoteOwner.GL);

  const title = useMemo(() => {
    if (ownerNote === NoteOwner.GL) return "Groundlight staff notes:";
    return `${ownerGroupName || "User"}'s Notes:`;
  }, [ownerNote, ownerGroupName]);

  const emptyNoteTitle = useMemo(() => {
    if (ownerNote === NoteOwner.GL) return "No Groundlight notes";
    return `No ${ownerGroupName || "User"}'s notes`;
  }, [ownerNote, ownerGroupName]);

  const showEmptyNote = notesList && notesList.length === 0 && !isLocalModeActive;

  const createNewOrder = () => {
    if (notesList && notesList.length > 0) {
      return notesList.length + 1;
    }
    return 1;
  };

  const onSaveFetchNote = async (note: SaveNoteData) => {
    createNote({
      ...note,
      note_type: ownerNote,
      order: createNewOrder(),
    });
  };

  const onSaveLocalNote = (note: SaveNoteData) => {
    try {
      if (setNotesData)
        setNotesData((prevNotes) => ({
          ...prevNotes,
          [ownerNote]: [
            {
              image: note.image || null,
              is_pinned: note.is_pinned || false,
              content: note.content,
              id: generateId(),
              note_type: ownerNote,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as LocalNote,
            ...prevNotes[ownerNote],
          ],
        }));
    } catch (error) {
      console.log(error);
    }
  };

  const onChangeOrder = async (items: Note[], owner: NoteOwner) => {
    if (setNotesData) setNotesData((prevNotes) => ({ ...prevNotes, [owner]: items }));
    if (!isLocalModeActive) {
      const itemsWithOrder = convertArrayToObject(items);
      updateNotesOrder(itemsWithOrder);
    }
  };

  const onChangeCustomerOrder = async (items: Note[]) => {
    setNotesList(items);

    onChangeOrder(items, NoteOwner.CUSTOMER);
  };

  useEffect(() => {
    setNotesList(notesData);
  }, [notesData]);

  const showCustomEditor =
    (!isOwner && canEditGLNotes && ownerNote === NoteOwner.GL) || (isOwner && ownerNote === NoteOwner.CUSTOMER);

  return (
    <>
      {showCustomEditor && (
        <CustomEditorNew
          defaultValueText=""
          onSaveNote={isLocalModeActive ? onSaveLocalNote : onSaveFetchNote}
          isLoading={isCreatingNote}
        />
      )}
      {isLoadingNotes && !notesList && (
        <Stack className="w-full gap-1">{SkeletonIndicator({ height: 130, loading: true })}</Stack>
      )}
      {!isLocalModeActive && title && (
        <Typography variant="title" color="#49494D" mt={3} mb={2}>
          {title}
        </Typography>
      )}
      {showEmptyNote && (
        <Box className="h-[150px] flex items-center justify-center">
          <Typography variant="subtitle" className="text-neutral-medium">
            {emptyNoteTitle}
          </Typography>
        </Box>
      )}
      {notesList && notesList.length > 0 && (
        <BlockUI active={isUpdatingNotesOrder}>
          <SortableList
            items={notesList}
            onChange={onChangeCustomerOrder}
            renderItem={(item) => (
              <SortableList.Item id={item.id}>
                <NoteItem
                  key={item.id}
                  data={item}
                  canModify={canModify}
                  showSortableDrag={notesList.length > 1}
                  isLocalModeActive={isLocalModeActive}
                  setNotesData={setNotesData}
                  ownerNote={ownerNote}
                  isOwner={isOwner}
                  canEditGLNotes={canEditGLNotes}
                  sortableDrag={SortableList.DragHandle}
                />
              </SortableList.Item>
            )}
          />
        </BlockUI>
      )}
    </>
  );
}
